import { useState, useCallback } from "react";
import Papa from "papaparse";
import { parse, format, isValid } from "date-fns";
import { supabase } from "@/lib/supabase";
import { getUnknownErrorMessage } from "@/lib/error-message";
import type { TablesInsert } from "@/types/database";

const BATCH_SIZE = 1000;

export type ImportType = "pages" | "spyserp";

export interface ImportStatus {
  type: "idle" | "success" | "error";
  message: string;
}

type CsvRecordRow = Record<string, string | undefined>;

type PageImportInsert = TablesInsert<"pages">;
type SpyserpRankingInsert = TablesInsert<"spyserp_rankings">;

const isCsvRecordRow = (row: unknown): row is CsvRecordRow =>
  typeof row === "object" && row !== null && !Array.isArray(row);

const readCsvCell = (
  row: CsvRecordRow,
  ...keys: string[]
): string | undefined => {
  for (const key of keys) {
    const value = row[key];
    if (value == null) continue;
    const trimmed = String(value).trim();
    if (trimmed !== "") return trimmed;
  }
  return undefined;
};

const parseCsvDate = (dateStr: string) => {
  if (!dateStr) return null;
  
  const trimmedDate = dateStr.trim();

  if (/^\d{2}\.\d{2}\.\d{4}$/.test(trimmedDate)) {
    const parsedDotDate = parse(trimmedDate, "dd.MM.yyyy", new Date());
    if (isValid(parsedDotDate)) {
      return format(parsedDotDate, "yyyy-MM-dd");
    }
  }

  const parsedDate = new Date(trimmedDate);
  if (isValid(parsedDate)) {
    return format(parsedDate, "yyyy-MM-dd");
  }

  return null;
};

const parseRank = (rankStr: string) => {
  if (!rankStr) return null;
  const str = rankStr.toString().trim();
  if (str === ">100") return 101;
  const num = parseInt(str, 10);
  return isNaN(num) ? null : num;
};

export const useSpyImport = () => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<ImportStatus>({
    type: "idle",
    message: "",
  });

  const processImport = useCallback(
    async (file: File, type: ImportType, fileInputRef?: React.RefObject<HTMLInputElement | null>) => {
      setLoading(true);
      setProgress(0);
      setStatus({ type: "idle", message: "Reading file..." });

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          try {
            const rawData = results.data.filter(isCsvRecordRow);
            const totalRows = rawData.length;

            if (totalRows === 0) throw new Error("CSV file is empty");

            if (type === "pages") {
              const formattedData: PageImportInsert[] = [];

              rawData.forEach((row) => {
                const public_url = readCsvCell(row, "Public URL", "public_url");
                const domain = readCsvCell(row, "domain");

                if (!public_url || !domain) return;

                formattedData.push({
                  domain,
                  public_url,
                  lang_page: readCsvCell(row, "lang_page") ?? null,
                  geo_page: readCsvCell(row, "geo_page") ?? null,
                  brand_keyword: readCsvCell(row, "brand_keyword") ?? null,
                });
              });

              if (formattedData.length === 0) {
                throw new Error("No valid data found to import");
              }

              setStatus({
                type: "idle",
                message: `Preparing to send ${formattedData.length} rows...`,
              });

              let processedCount = 0;
              for (let i = 0; i < formattedData.length; i += BATCH_SIZE) {
                const batch = formattedData.slice(i, i + BATCH_SIZE);

                const { error } = await supabase.from("pages").upsert(batch, {
                  onConflict: "public_url",
                  ignoreDuplicates: true,
                });

                if (error) throw error;

                await new Promise((resolve) => setTimeout(resolve, 150));

                processedCount += batch.length;
                setProgress(
                  Math.round((processedCount / formattedData.length) * 100),
                );
              }

              await supabase.rpc("refresh_spyserp_filters");
              await supabase.rpc("refresh_assigned_segments");

              setStatus({
                type: "success",
                message: `Successfully uploaded ${processedCount} records!`,
              });
              return;
            }

            const formattedData: SpyserpRankingInsert[] = [];

            rawData.forEach((row) => {
              const page_url = readCsvCell(row, "SERP Url", "SERP URL");
              const domain = readCsvCell(row, "Domains") ?? "";
              const keyword = readCsvCell(row, "Keywords");
              const location = readCsvCell(row, "Location") ?? null;
              const search_engine =
                readCsvCell(row, "Search engines") ?? null;

              if (!page_url || !keyword) return;

              const checkDateRaw = readCsvCell(row, "Check Date");
              const rankRaw = row["Rank"];

              if (checkDateRaw && rankRaw !== undefined) {
                const check_date = parseCsvDate(checkDateRaw);
                if (check_date) {
                  formattedData.push({
                    page_url,
                    domain,
                    keyword,
                    rank: parseRank(rankRaw),
                    check_date,
                    location,
                    search_engine,
                  });
                }
                return;
              }

              const dateColumnRegex = /^(\d{2})\.(\d{2})\.(\d{4})$/;

              Object.keys(row).forEach((colName) => {
                if (!dateColumnRegex.test(colName)) return;

                const rankVal = row[colName];
                if (rankVal === undefined || rankVal === "") return;

                const parsedDate = parse(colName, "dd.MM.yyyy", new Date());
                const check_date = format(parsedDate, "yyyy-MM-dd");

                formattedData.push({
                  page_url,
                  domain,
                  keyword,
                  rank: parseRank(rankVal),
                  check_date,
                  location,
                  search_engine,
                });
              });
            });

            if (formattedData.length === 0) {
              throw new Error("No valid data found to import");
            }

            setStatus({
              type: "idle",
              message: `Preparing to send ${formattedData.length} rows...`,
            });

            let processedCount = 0;
            for (let i = 0; i < formattedData.length; i += BATCH_SIZE) {
              const batch = formattedData.slice(i, i + BATCH_SIZE);

              const { error } = await supabase
                .from("spyserp_rankings")
                .upsert(batch, {
                  onConflict:
                    "page_url, keyword, search_engine, location, check_date",
                  ignoreDuplicates: true,
                });

              if (error) throw error;

              await new Promise((resolve) => setTimeout(resolve, 150));

              processedCount += batch.length;
              setProgress(
                Math.round((processedCount / formattedData.length) * 100),
              );
            }

            await supabase.rpc("refresh_spyserp_filters");
            await supabase.rpc("refresh_assigned_segments");

            setStatus({
              type: "success",
              message: `Successfully uploaded ${processedCount} records!`,
            });
          } catch (error: unknown) {
            console.error("Import error:", error);
            setStatus({
              type: "error",
              message: `Error: ${getUnknownErrorMessage(error)}`,
            });
          } finally {
            setLoading(false);
            if (fileInputRef?.current) fileInputRef.current.value = "";
          }
        },
        error: (error) => {
          setLoading(false);
          setStatus({
            type: "error",
            message: `Error reading file: ${error.message}`,
          });
        },
      });
    },
    []
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, type: ImportType, fileInputRef?: React.RefObject<HTMLInputElement | null>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      processImport(file, type, fileInputRef);
    },
    [processImport]
  );

  return {
    loading,
    progress,
    status,
    handleFileSelect,
  };
};